import { useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ScanLine } from 'lucide-react';
import api from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ConfirmButton } from '../StaffAppItemDetail';
import BarcodeScanner from './BarcodeScanner';
import ToolHeader, { Spinner } from './ToolHeader';

const realBatches = (p) => (p.batches || []).filter((b) => b.batch_number);

const Field = ({ label, id, children }) => (
  <div className="space-y-1.5"><Label htmlFor={id} className="text-xs">{label}</Label>{children}</div>
);

export default function ProductEditPage() {
  const { productId } = useParams();
  const { tools } = useOutletContext();
  const [product, setProduct] = useState(null);
  const [form, setForm] = useState(null);
  const [batches, setBatches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const canEdit = !!tools?.products_edit;

  const load = async (isActive = () => true) => {
    const { data } = await api.get(`/products/${productId}`);
    if (!isActive()) return;
    setProduct(data);
    setForm({ name: data.name || '', manufacturer: data.manufacturer || '', barcode: data.barcode || '', stock_quantity: data.stock_quantity ?? 0 });
    setBatches(realBatches(data).map((b) => ({ batch_number: b.batch_number, expiry_date: (b.expiry_date || '').slice(0, 10), quantity: b.quantity ?? 0 })));
  };
  useEffect(() => {
    let active = true;
    load(() => active).catch(() => toast.error('Could not load this product'));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  if (!form) return <><ToolHeader title="Product" back="/staff-app/tools/products" /><Spinner /></>;
  const hasBatches = batches.length > 0;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setBatch = (i, k, v) => setBatches(batches.map((b, j) => (j === i ? { ...b, [k]: v } : b)));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      ['name', 'manufacturer', 'barcode'].forEach((k) => { if (form[k].trim() !== (product[k] || '')) patch[k] = form[k].trim(); });
      if (!hasBatches && Number(form.stock_quantity) !== (product.stock_quantity ?? 0)) patch.stock_quantity = parseInt(form.stock_quantity, 10) || 0;
      if (patch.name === '') throw new Error('Product name cannot be empty');
      if (Object.keys(patch).length) await api.put(`/products/${productId}`, patch);
      const orig = realBatches(product);
      for (const b of batches) {
        const o = orig.find((x) => x.batch_number === b.batch_number) || {};
        if (b.expiry_date !== (o.expiry_date || '').slice(0, 10) || Number(b.quantity) !== (o.quantity ?? 0)) {
          await api.put(`/products/${productId}/batches/${encodeURIComponent(b.batch_number)}`, { expiry_date: b.expiry_date, quantity: parseInt(b.quantity, 10) || 0 });
        }
      }
      toast.success('Product updated');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not save changes');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="staff-app-product-edit">
      <ToolHeader title={product.name} subtitle={canEdit ? 'Edit product' : 'View only'} back="/staff-app/tools/products" />
      <div className="p-4 space-y-4">
        <Field label="Product name" id="pe-name"><Input id="pe-name" value={form.name} onChange={set('name')} disabled={!canEdit} data-testid="staff-app-product-name" /></Field>
        <Field label="Manufacturer" id="pe-mfr"><Input id="pe-mfr" value={form.manufacturer} onChange={set('manufacturer')} disabled={!canEdit} data-testid="staff-app-product-manufacturer" /></Field>
        <Field label="Barcode" id="pe-bc">
          <div className="flex gap-2">
            <Input id="pe-bc" value={form.barcode} onChange={set('barcode')} disabled={!canEdit} inputMode="numeric" data-testid="staff-app-product-barcode" />
            {canEdit && <button type="button" onClick={() => setScanning(true)} aria-label="Scan barcode" data-testid="staff-app-product-barcode-scan"
              className="h-10 w-10 shrink-0 rounded-md border border-border flex items-center justify-center"><ScanLine className="w-4 h-4" /></button>}
          </div>
        </Field>
        {!hasBatches && (
          <Field label="Quantity in stock" id="pe-qty"><Input id="pe-qty" type="number" min="0" inputMode="numeric" value={form.stock_quantity} onChange={set('stock_quantity')} disabled={!canEdit} data-testid="staff-app-product-quantity" /></Field>
        )}
        {hasBatches && (
          <div className="space-y-2" data-testid="staff-app-product-batches">
            <p className="text-xs font-semibold">Batches · total {batches.reduce((s, b) => s + (parseInt(b.quantity, 10) || 0), 0)} in stock</p>
            {batches.map((b, i) => (
              <div key={b.batch_number} className="rounded-xl border border-border bg-card p-3 space-y-2" data-testid={`staff-app-batch-${b.batch_number}`}>
                <p className="text-xs font-semibold">Batch {b.batch_number}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Expiry date" id={`pe-exp-${i}`}><Input id={`pe-exp-${i}`} type="date" value={b.expiry_date} onChange={(e) => setBatch(i, 'expiry_date', e.target.value)} disabled={!canEdit} data-testid={`staff-app-batch-expiry-${b.batch_number}`} /></Field>
                  <Field label="Quantity" id={`pe-bq-${i}`}><Input id={`pe-bq-${i}`} type="number" min="0" inputMode="numeric" value={b.quantity} onChange={(e) => setBatch(i, 'quantity', e.target.value)} disabled={!canEdit} data-testid={`staff-app-batch-qty-${b.batch_number}`} /></Field>
                </div>
              </div>
            ))}
          </div>
        )}
        {canEdit && (
          <div className="flex pt-2">
            <ConfirmButton label="Save changes" title="Save product changes?" description="This updates the product record for everyone in this branch."
              onConfirm={save} loading={saving} testId="staff-app-product-save" />
          </div>
        )}
      </div>
      {scanning && <BarcodeScanner onClose={() => setScanning(false)} onDetected={(c) => { setScanning(false); setForm((f) => ({ ...f, barcode: c })); }} />}
    </div>
  );
}
